import { Building2, Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'disparos:tenant-switching';
// Tempo máximo do overlay (defense-in-depth caso /me trave). Generoso pra
// não cortar fade-out cinematográfico nem deixar overlay órfão.
const MAX_DURATION_MS = 16_000;
// Tempo MÍNIMO da fase "show" (loading) antes de aceitar success. Sem isso,
// /me pode retornar o tenant alvo logo após reload e o user nem vê o estado
// "Trocando workspace…" — pula direto pra check verde em ~50ms.
const MIN_SHOW_DURATION_MS = 700;
// Tempo de exibição do estado "success" (check verde) antes de fade-out.
const SUCCESS_DISPLAY_MS = 800;
// Duração total da fase "hide" (fade out + cleanup) — bate com a maior
// CSS animation de saída (350ms backdrop) + folga pra cleanup.
const HIDE_DURATION_MS = 450;

interface SwitchSnapshot {
  toTenantName: string;
  toTenantId: string;
  until: number;
}

/**
 * Sinaliza início do switch — chamado por `useSwitchTenant` antes do
 * `window.location.reload()`. Persiste em `sessionStorage` pra sobreviver
 * ao reload da página.
 */
export function markTenantSwitchStart(toTenantId: string, toTenantName: string): void {
  const snapshot: SwitchSnapshot = {
    toTenantId,
    toTenantName,
    until: Date.now() + MAX_DURATION_MS,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
}

function readSnapshot(): SwitchSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SwitchSnapshot;
    if (typeof parsed.until !== 'number' || parsed.until < Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

interface TenantSwitchOverlayProps {
  /**
   * Quando o `/me` retornar este tenantId, fechamos o overlay (com fade out
   * suave). AppShell passa `me.data?.currentTenant.tenantId`.
   */
  currentTenantId: string | undefined;
}

/**
 * Overlay full-screen exibido durante a transição entre workspaces. Cobre o
 * gap visual do `window.location.reload()` (tela branca) com card centralizado
 * mostrando spinner + nome do workspace destino. Esconde com fade-out quando
 * detectar que o backend já está retornando o tenant alvo.
 */
export function TenantSwitchOverlay({ currentTenantId }: TenantSwitchOverlayProps) {
  const [snapshot, setSnapshot] = useState<SwitchSnapshot | null>(() => readSnapshot());
  const [phase, setPhase] = useState<'show' | 'success' | 'hide'>(() =>
    snapshot ? 'show' : 'hide',
  );
  // `mounted` flip pra `true` no próximo frame após mount — dispara CSS
  // transition de entrada (opacity + scale + translate). Sem isso, mount já
  // chega com classes "visíveis" e nenhuma animação acontece.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (!snapshot) return;
    const r = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(r);
  }, [snapshot]);

  // Marca o instante do mount pra impor MIN_SHOW_DURATION_MS — garante que a
  // fase loading sempre apareça por tempo suficiente, mesmo que /me retorne
  // o tenant alvo logo após reload.
  const [showStartedAt] = useState(() => Date.now());

  // Detecta quando o tenant alvo já está ativo: mostra check verde por
  // SUCCESS_DISPLAY_MS antes de fade-out, dando confirmação visual.
  useEffect(() => {
    if (!snapshot) return;
    if (currentTenantId !== snapshot.toTenantId) return;

    const elapsed = Date.now() - showStartedAt;
    const minShowRemaining = Math.max(0, MIN_SHOW_DURATION_MS - elapsed);

    // Espera o restante do MIN_SHOW antes de virar success.
    const tShow = setTimeout(() => setPhase('success'), minShowRemaining);
    // Mantém success por SUCCESS_DISPLAY_MS, depois inicia fade-out.
    const tHide = setTimeout(() => setPhase('hide'), minShowRemaining + SUCCESS_DISPLAY_MS);
    // Cleanup final após fade-out completo.
    const tCleanup = setTimeout(
      () => {
        sessionStorage.removeItem(STORAGE_KEY);
        setSnapshot(null);
      },
      minShowRemaining + SUCCESS_DISPLAY_MS + HIDE_DURATION_MS,
    );

    return () => {
      clearTimeout(tShow);
      clearTimeout(tHide);
      clearTimeout(tCleanup);
    };
  }, [currentTenantId, snapshot, showStartedAt]);

  // Timeout de segurança — mesmo se /me não chegar a tempo, fecha após
  // MAX_DURATION_MS (evita overlay travado se request falhar).
  useEffect(() => {
    if (!snapshot) return;
    const remaining = Math.max(0, snapshot.until - Date.now());
    const t = setTimeout(() => {
      sessionStorage.removeItem(STORAGE_KEY);
      setSnapshot(null);
    }, remaining);
    return () => clearTimeout(t);
  }, [snapshot]);

  if (!snapshot) return null;

  // 3 estados visuais distintos:
  // - entering (mount) → animação keyframe enter (60% bounce sutil + blur→0)
  // - showing/success → estático
  // - exiting → keyframe exit multi-step (segura tensão + dispara final)
  const isExiting = phase === 'hide';
  const showCard = mounted;

  // Easing linear no exit — dissolução uniforme rápida.
  const backdropAnimation = isExiting ? 'overlay-backdrop-exit 350ms linear forwards' : undefined;
  const cardAnimation = isExiting
    ? 'overlay-card-exit 280ms linear forwards'
    : showCard
      ? 'overlay-card-enter 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards'
      : undefined;

  return (
    <output
      className={`fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-md ${
        isExiting ? 'pointer-events-none' : ''
      } ${!isExiting && !showCard ? 'opacity-0' : ''} ${
        !isExiting && showCard
          ? 'opacity-100 transition-opacity duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]'
          : ''
      }`}
      style={backdropAnimation ? { animation: backdropAnimation } : undefined}
      aria-live="polite"
    >
      <div
        className="flex min-w-[300px] max-w-md flex-col items-center gap-5 rounded-2xl border bg-card p-10 shadow-2xl will-change-transform"
        style={{
          animation: cardAnimation,
          // Pré-mount opacity 0 evita flash do estado inicial sem animação.
          opacity: cardAnimation ? undefined : 0,
        }}
      >
        <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <div
            className={`absolute inset-0 flex items-center justify-center transition-all duration-[1200ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
              phase === 'success' ? 'opacity-0 scale-50' : 'opacity-100 scale-100'
            }`}
          >
            <Building2 className="h-8 w-8 text-primary" />
            <Loader2 className="absolute h-16 w-16 text-primary/40 [animation:spin_2.2s_linear_infinite]" />
          </div>
          <div
            className={`absolute inset-0 flex items-center justify-center transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
              phase === 'success' ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
            }`}
          >
            <Check className="h-8 w-8 text-emerald-600 dark:text-emerald-500" />
          </div>
        </div>
        <div className="space-y-1.5 text-center">
          <p className="text-sm font-medium text-muted-foreground transition-colors duration-[1000ms]">
            {phase === 'success' ? 'Workspace ativo' : 'Trocando workspace…'}
          </p>
          <p className="text-xl font-semibold tracking-tight">{snapshot.toTenantName}</p>
        </div>
      </div>
    </output>
  );
}
